import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import BackupSettingsCard from '../../components/backup/BackupSettingsCard';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  CircleNotch,
  ClockCounterClockwise,
  DownloadSimple,
  Trash,
  Shield,
  CloudArrowUp,
  Database,
  Gear,
  MagnifyingGlass,
  Funnel,
  User,
  Calendar,
  FileArchive,
  Check,
  X,
  Warning,
  Info,
  Play,
  ArrowsClockwise,
  Cloud,
  HardDrive,
  Eye,
  Upload,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audit action colors and icons
const ACTION_STYLES = {
  LOGIN: { color: 'bg-green-500/20 text-green-400', icon: User },
  LOGOUT: { color: 'bg-gray-500/20 text-gray-400', icon: User },
  LOGIN_FAILED: { color: 'bg-red-500/20 text-red-400', icon: Warning },
  CREATE: { color: 'bg-blue-500/20 text-blue-400', icon: Check },
  UPDATE: { color: 'bg-yellow-500/20 text-yellow-400', icon: ArrowsClockwise },
  DELETE: { color: 'bg-red-500/20 text-red-400', icon: Trash },
  VIEW: { color: 'bg-purple-500/20 text-purple-400', icon: Eye },
  EXPORT: { color: 'bg-cyan-500/20 text-cyan-400', icon: DownloadSimple },
  IMPORT: { color: 'bg-teal-500/20 text-teal-400', icon: Upload },
  BACKUP: { color: 'bg-indigo-500/20 text-indigo-400', icon: Database },
  RESTORE: { color: 'bg-orange-500/20 text-orange-400', icon: ClockCounterClockwise },
  SETTINGS_CHANGE: { color: 'bg-pink-500/20 text-pink-400', icon: Gear },
  PERMISSION_CHANGE: { color: 'bg-rose-500/20 text-rose-400', icon: Shield },
  PASSWORD_CHANGE: { color: 'bg-amber-500/20 text-amber-400', icon: Shield },
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function AuditLogsPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(true);
  
  // Audit Logs State
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsOffset, setLogsOffset] = useState(0);
  const [logFilters, setLogFilters] = useState({
    action: '',
    entity_type: '',
    search: '',
    start_date: '',
    end_date: '',
    user_id: '',
  });
  const [companyUsers, setCompanyUsers] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  
  // Backups State
  const [backups, setBackups] = useState([]);
  const [backupSettings, setBackupSettings] = useState(null);
  const [cloudConfig, setCloudConfig] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [cloudConfigDialogOpen, setCloudConfigDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCompanyUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`, { headers });
      // Endpoint returns a list of users in the caller's company. Sort by
      // display name so the dropdown is friendly.
      const list = (res.data || []).map(u => ({
        id: u.id,
        label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        email: u.email,
      })).sort((a, b) => a.label.localeCompare(b.label));
      setCompanyUsers(list);
    } catch (err) {
      // Non-fatal — dropdown just stays empty
      console.error('Failed to load company users for audit filter', err);
    }
  };

  // Fetch audit logs
  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (logFilters.action) params.append('action', logFilters.action);
      if (logFilters.entity_type) params.append('entity_type', logFilters.entity_type);
      if (logFilters.search) params.append('search', logFilters.search);
      if (logFilters.start_date) params.append('start_date', logFilters.start_date);
      if (logFilters.end_date) params.append('end_date', logFilters.end_date);
      if (logFilters.user_id) params.append('user_id', logFilters.user_id);
      params.append('limit', '50');
      params.append('offset', logsOffset.toString());
      
      const res = await axios.get(`${API_URL}/api/audit/logs?${params}`, { headers });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      toast.error('Failed to load audit logs');
    }
  };

  // Fetch audit stats
  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audit/logs/stats?days=30`, { headers });
      setAuditStats(res.data);
    } catch (err) {
      console.error('Failed to fetch audit stats:', err);
    }
  };

  // Fetch backups
  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audit/backups`, { headers });
      setBackups(res.data.backups || []);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
      toast.error('Failed to load backups');
    }
  };

  // Fetch backup settings
  const fetchBackupSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audit/backups/settings`, { headers });
      setBackupSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch backup settings:', err);
    }
  };

  // Fetch cloud config
  const fetchCloudConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audit/backups/cloud-config`, { headers });
      setCloudConfig(res.data);
    } catch (err) {
      console.error('Failed to fetch cloud config:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchLogs(),
        fetchStats(),
        fetchBackups(),
        fetchBackupSettings(),
        fetchCloudConfig(),
        fetchCompanyUsers(),
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchLogs();
    }
  }, [logsOffset, logFilters]);

  // Create manual backup
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const includeAtt = includeAttachments ? 'true' : 'false';
      const res = await axios.post(
        `${API_URL}/api/audit/backups/create?include_users=true&include_attachments=${includeAtt}`,
        {},
        { headers },
      );
      toast.success(`Backup started ${includeAttachments ? '(with attachments)' : '(data only)'}`);
      // Refresh backups after a delay to show progress
      setTimeout(fetchBackups, 2000);
      setTimeout(fetchBackups, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  // Download backup
  const handleDownload = async (backup) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/audit/backups/${backup.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download backup');
    }
  };

  // Delete backup
  const handleDeleteBackup = async (backupId) => {
    if (!confirm('Are you sure you want to delete this backup? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/audit/backups/${backupId}`, { headers });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };

  // Restore from backup
  const handleRestore = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    if (!file) {
      toast.error('Please select a backup file');
      return;
    }
    
    if (!confirm('WARNING: This will replace all current data with the backup. Are you absolutely sure?')) return;
    
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('restore_users', 'false');
      
      const res = await axios.post(
        `${API_URL}/api/audit/backups/restore`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`Restore completed! ${res.data.total_records} records restored.`);
      setRestoreDialogOpen(false);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  // Update backup settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      params.append('auto_backup_enabled', backupSettings.auto_backup_enabled);
      params.append('backup_time', backupSettings.backup_time);
      params.append('retention_days', backupSettings.retention_days);
      params.append('include_users', backupSettings.include_users);
      params.append('cloud_backup_enabled', backupSettings.cloud_backup_enabled);
      
      await axios.post(`${API_URL}/api/audit/backups/settings?${params}`, {}, { headers });
      toast.success('Backup settings saved');
      setSettingsDialogOpen(false);
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  // Save cloud config
  const handleSaveCloudConfig = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const params = new URLSearchParams();
      const provider = formData.get('provider');
      // Convert 'none' to empty string for API
      const providerValue = provider === 'none' ? '' : (provider || '');
      params.append('provider', providerValue);
      
      if (provider === 'aws_s3') {
        params.append('aws_access_key_id', formData.get('aws_access_key_id') || '');
        params.append('aws_secret_access_key', formData.get('aws_secret_access_key') || '');
        params.append('aws_bucket', formData.get('aws_bucket') || '');
        params.append('aws_region', formData.get('aws_region') || 'us-east-1');
        params.append('aws_prefix', formData.get('aws_prefix') || 'backups');
      } else if (provider === 'gcs') {
        params.append('gcs_bucket', formData.get('gcs_bucket') || '');
        params.append('gcs_prefix', formData.get('gcs_prefix') || 'backups');
        const credsJson = formData.get('gcs_credentials_json');
        if (credsJson) {
          params.append('gcs_credentials_json', credsJson);
        }
      }
      
      await axios.post(`${API_URL}/api/audit/backups/cloud-config?${params}`, {}, { headers });
      toast.success('Cloud storage configuration saved');
      setCloudConfigDialogOpen(false);
      fetchCloudConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save cloud config');
    }
  };

  // Test cloud connection
  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await axios.post(`${API_URL}/api/audit/backups/cloud-config/test`, {}, { headers });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Audit & Backup
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor system activity and manage backups
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <ClockCounterClockwise className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{auditStats?.total_events || 0}</p>
              <p className="text-xs text-muted-foreground">Events (30 days)</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <User className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{auditStats?.login_count || 0}</p>
              <p className="text-xs text-muted-foreground">Logins</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <Warning className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{auditStats?.failed_login_count || 0}</p>
              <p className="text-xs text-muted-foreground">Failed Logins</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <Database className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{backups.length}</p>
              <p className="text-xs text-muted-foreground">Backups</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-1 max-w-xs">
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <ClockCounterClockwise className="w-4 h-4" />
            Audit Logs
          </TabsTrigger>
        </TabsList>
        <p className="text-xs text-muted-foreground mt-2 mb-4">
          Looking for backups? They&apos;ve moved to{' '}
          <a href="/dashboard/backup-recovery" className="text-primary underline">Backup &amp; Recovery</a>.
        </p>

        {/* Audit Logs Tab */}
        <TabsContent value="logs" className="mt-6">
          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative md:col-span-2">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search logs..."
                  className="pl-10"
                  value={logFilters.search}
                  onChange={(e) => setLogFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              
              <Select
                value={logFilters.action}
                onValueChange={(value) => setLogFilters(prev => ({ ...prev, action: value === 'all' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="LOGIN">Login</SelectItem>
                  <SelectItem value="CREATE">Create</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                  <SelectItem value="BACKUP">Backup</SelectItem>
                  <SelectItem value="RESTORE">Restore</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={logFilters.start_date}
                onChange={(e) => setLogFilters(prev => ({ ...prev, start_date: e.target.value }))}
                placeholder="Start Date"
              />

              <Input
                type="date"
                value={logFilters.end_date}
                onChange={(e) => setLogFilters(prev => ({ ...prev, end_date: e.target.value }))}
                placeholder="End Date"
              />

              {/* Filter by user — Admin selects a team member to see every entry that person made */}
              <Select
                value={logFilters.user_id || 'all'}
                onValueChange={(value) => setLogFilters(prev => ({ ...prev, user_id: value === 'all' ? '' : value }))}
              >
                <SelectTrigger data-testid="audit-user-filter">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {companyUsers.map(u => (
                    <SelectItem key={u.id} value={u.id} data-testid={`audit-user-option-${u.id}`}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Timestamp</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Action</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Entity</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground">
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const actionStyle = ACTION_STYLES[log.action] || { color: 'bg-gray-500/20 text-gray-400', icon: Info };
                      const ActionIcon = actionStyle.icon;
                      const isExpanded = expandedLog === log.id;
                      
                      return (
                        <tr 
                          key={log.id} 
                          className="border-b border-border hover:bg-muted/30 cursor-pointer"
                          onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{formatDate(log.timestamp)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${actionStyle.color} gap-1`}>
                              <ActionIcon className="w-3 h-3" />
                              {log.action}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm">{log.entity_type}</td>
                          <td className="py-3 px-4 text-sm">{log.user_email || 'System'}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                                {log.action_description}
                              </span>
                              {isExpanded ? <CaretUp className="w-4 h-4" /> : <CaretDown className="w-4 h-4" />}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {logsTotal > 50 && (
              <div className="flex items-center justify-between p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {logsOffset + 1}-{Math.min(logsOffset + 50, logsTotal)} of {logsTotal}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsOffset === 0}
                    onClick={() => setLogsOffset(Math.max(0, logsOffset - 50))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsOffset + 50 >= logsTotal}
                    onClick={() => setLogsOffset(logsOffset + 50)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Backups Tab */}
        <TabsContent value="backups" className="mt-6">
          {/* Auto-download to PC + Per-user Google Drive settings */}
          <BackupSettingsCard
            backups={backups}
            onRefresh={fetchBackups}
            downloadBackup={handleDownload}
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold">Backup Management</h2>
              <p className="text-sm text-muted-foreground">
                Create, download, and restore backups of your company data
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border bg-muted/30 cursor-pointer select-none" data-testid="backup-include-att-wrap">
                <input
                  type="checkbox"
                  checked={includeAttachments}
                  onChange={(e) => setIncludeAttachments(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                  data-testid="backup-include-attachments"
                />
                Include attachments
                <span className="text-[10px] text-muted-foreground">
                  ({includeAttachments ? 'PDFs, logos, uploads' : 'data only, smaller'})
                </span>
              </label>
              <Button
                variant="outline"
                onClick={() => setRestoreDialogOpen(true)}
              >
                <ClockCounterClockwise className="w-4 h-4 mr-2" />
                Restore
              </Button>
              <Button
                onClick={handleCreateBackup}
                disabled={creatingBackup}
                data-testid="create-backup-btn"
              >
                {creatingBackup ? (
                  <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Create Backup
              </Button>
            </div>
          </div>

          {/* Backup Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <HardDrive className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Local Storage</p>
                  <p className="text-lg font-semibold text-foreground">
                    {backups.filter(b => b.file_exists).length} backups
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Cloud className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cloud Storage</p>
                  <p className="text-lg font-semibold text-foreground">
                    {cloudConfig?.configured ? cloudConfig.provider?.toUpperCase() : 'Not configured'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <ArrowsClockwise className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Auto Backup</p>
                  <p className="text-lg font-semibold text-foreground">
                    {backupSettings?.auto_backup_enabled ? (
                      <span className="text-green-400">Enabled</span>
                    ) : (
                      <span className="text-muted-foreground">Disabled</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Backups Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Filename</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Size</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Cloud</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        No backups found. Create your first backup!
                      </td>
                    </tr>
                  ) : (
                    backups.map((backup) => (
                      <tr key={backup.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileArchive className="w-4 h-4 text-primary" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{backup.filename}</span>
                              {backup.record_count != null && (
                                <span className="text-[10px] text-muted-foreground">
                                  {backup.record_count} records
                                  {backup.attachment_count > 0 && ` · ${backup.attachment_count} attachments`}
                                  {backup.version === '2.0' && ' · v2'}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={backup.backup_type === 'auto' ? 'secondary' : 'default'}>
                            {backup.backup_type === 'auto' ? 'Auto' : 'Manual'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {backup.status === 'completed' ? (
                            <Badge className="bg-green-500/20 text-green-400">
                              <Check className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          ) : backup.status === 'in_progress' ? (
                            <Badge className="bg-yellow-500/20 text-yellow-400">
                              <CircleNotch className="w-3 h-3 mr-1 animate-spin" />
                              In Progress
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">
                              <X className="w-3 h-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {formatFileSize(backup.file_size)}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {formatDate(backup.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          {backup.cloud_uploaded ? (
                            <Badge className="bg-blue-500/20 text-blue-400">
                              <Cloud className="w-3 h-3 mr-1" />
                              Uploaded
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            {backup.file_exists && backup.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(backup)}
                              >
                                <DownloadSimple className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteBackup(backup.id)}
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Backup Settings */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <ArrowsClockwise className="w-5 h-5 text-primary" />
                  Auto Backup Settings
                </h3>
                <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
                  <Gear className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Auto Backup</span>
                  <Badge className={backupSettings?.auto_backup_enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                    {backupSettings?.auto_backup_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Backup Time (UTC)</span>
                  <span className="text-sm font-medium">{backupSettings?.backup_time || '02:00'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Retention Period</span>
                  <span className="text-sm font-medium">{backupSettings?.retention_days || 30} days</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Include Users</span>
                  <Badge className={backupSettings?.include_users ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}>
                    {backupSettings?.include_users ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Cloud Backup</span>
                  <Badge className={backupSettings?.cloud_backup_enabled ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}>
                    {backupSettings?.cloud_backup_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Cloud Storage */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-blue-400" />
                  Cloud Storage
                </h3>
                <div className="flex gap-2">
                  {cloudConfig?.configured && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                    >
                      {testingConnection ? (
                        <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Test
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setCloudConfigDialogOpen(true)}>
                    <Gear className="w-4 h-4 mr-2" />
                    Configure
                  </Button>
                </div>
              </div>

              {cloudConfig?.configured ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Provider</span>
                    <Badge className="bg-blue-500/20 text-blue-400">
                      {cloudConfig.provider === 'aws_s3' ? 'AWS S3' : 'Google Cloud Storage'}
                    </Badge>
                  </div>
                  {cloudConfig.provider === 'aws_s3' && (
                    <>
                      <div className="flex items-center justify-between py-2 border-b border-border">
                        <span className="text-sm text-muted-foreground">Bucket</span>
                        <span className="text-sm font-medium">{cloudConfig.aws_bucket}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border">
                        <span className="text-sm text-muted-foreground">Region</span>
                        <span className="text-sm font-medium">{cloudConfig.aws_region}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-muted-foreground">Prefix</span>
                        <span className="text-sm font-medium">{cloudConfig.aws_prefix || 'backups'}</span>
                      </div>
                    </>
                  )}
                  {cloudConfig.provider === 'gcs' && (
                    <>
                      <div className="flex items-center justify-between py-2 border-b border-border">
                        <span className="text-sm text-muted-foreground">Bucket</span>
                        <span className="text-sm font-medium">{cloudConfig.gcs_bucket}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-muted-foreground">Prefix</span>
                        <span className="text-sm font-medium">{cloudConfig.gcs_prefix || 'backups'}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Cloud className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Cloud storage not configured. Configure AWS S3 or Google Cloud Storage for off-site backups.
                  </p>
                  <Button onClick={() => setCloudConfigDialogOpen(true)}>
                    <CloudArrowUp className="w-4 h-4 mr-2" />
                    Setup Cloud Storage
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise className="w-5 h-5 text-orange-400" />
              Restore from Backup
            </DialogTitle>
            <DialogDescription>
              Upload a backup file to restore your company data. This will replace all existing data.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRestore}>
            <div className="space-y-4 py-4">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex gap-2">
                  <Warning className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <div className="text-sm text-orange-300">
                    <p className="font-semibold">Warning!</p>
                    <p>This action will replace all current data with the backup. This cannot be undone.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Backup File (.zip)</Label>
                <Input
                  type="file"
                  accept=".zip"
                  ref={fileInputRef}
                  className="cursor-pointer"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRestoreDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={restoring}>
                {restoring ? (
                  <>
                    <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <ClockCounterClockwise className="w-4 h-4 mr-2" />
                    Restore
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Backup Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gear className="w-5 h-5 text-primary" />
              Backup Settings
            </DialogTitle>
            <DialogDescription>
              Configure automatic backup schedule and retention policy
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveSettings}>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Auto Backup</Label>
                  <p className="text-xs text-muted-foreground">Automatically backup daily</p>
                </div>
                <Switch
                  checked={backupSettings?.auto_backup_enabled || false}
                  onCheckedChange={(checked) => setBackupSettings(prev => ({ ...prev, auto_backup_enabled: checked }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Backup Time (UTC)</Label>
                <Input
                  type="time"
                  value={backupSettings?.backup_time || '02:00'}
                  onChange={(e) => setBackupSettings(prev => ({ ...prev, backup_time: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Retention Period (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={backupSettings?.retention_days || 30}
                  onChange={(e) => setBackupSettings(prev => ({ ...prev, retention_days: parseInt(e.target.value) }))}
                />
                <p className="text-xs text-muted-foreground">Backups older than this will be automatically deleted</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Include Users</Label>
                  <p className="text-xs text-muted-foreground">Backup user accounts</p>
                </div>
                <Switch
                  checked={backupSettings?.include_users || false}
                  onCheckedChange={(checked) => setBackupSettings(prev => ({ ...prev, include_users: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Cloud Backup</Label>
                  <p className="text-xs text-muted-foreground">Upload to cloud storage</p>
                </div>
                <Switch
                  checked={backupSettings?.cloud_backup_enabled || false}
                  onCheckedChange={(checked) => setBackupSettings(prev => ({ ...prev, cloud_backup_enabled: checked }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Settings
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cloud Config Dialog */}
      <Dialog open={cloudConfigDialogOpen} onOpenChange={setCloudConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-400" />
              Cloud Storage Configuration
            </DialogTitle>
            <DialogDescription>
              Configure AWS S3 or Google Cloud Storage for off-site backups
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveCloudConfig}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Cloud Provider</Label>
                <Select name="provider" defaultValue={cloudConfig?.provider || 'none'}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Disable)</SelectItem>
                    <SelectItem value="aws_s3">AWS S3</SelectItem>
                    <SelectItem value="gcs">Google Cloud Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="font-medium mb-3">AWS S3 Configuration</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Access Key ID</Label>
                    <Input name="aws_access_key_id" placeholder="AKIAIOSFODNN7EXAMPLE" />
                  </div>
                  <div className="space-y-2">
                    <Label>Secret Access Key</Label>
                    <PasswordInput name="aws_secret_access_key" placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" />
                  </div>
                  <div className="space-y-2">
                    <Label>Bucket Name *</Label>
                    <Input name="aws_bucket" placeholder="my-backup-bucket" defaultValue={cloudConfig?.aws_bucket || ''} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Region</Label>
                      <Input name="aws_region" placeholder="us-east-1" defaultValue={cloudConfig?.aws_region || 'us-east-1'} />
                    </div>
                    <div className="space-y-2">
                      <Label>Prefix</Label>
                      <Input name="aws_prefix" placeholder="backups" defaultValue={cloudConfig?.aws_prefix || 'backups'} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="font-medium mb-3">Google Cloud Storage Configuration</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Bucket Name *</Label>
                    <Input name="gcs_bucket" placeholder="my-backup-bucket" defaultValue={cloudConfig?.gcs_bucket || ''} />
                  </div>
                  <div className="space-y-2">
                    <Label>Prefix</Label>
                    <Input name="gcs_prefix" placeholder="backups" defaultValue={cloudConfig?.gcs_prefix || 'backups'} />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Account JSON (optional)</Label>
                    <Textarea 
                      name="gcs_credentials_json" 
                      placeholder='{"type": "service_account", ...}'
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground">Paste the contents of your service account JSON key file</p>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloudConfigDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Configuration
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
